import beUpgradePlan from 'actions/billing/beUpgradePlan.action';
import feSetThemeMode from 'actions/salon/feSetThemeMode.action';
import { POST_SALON_DATA_SUCCESS } from 'actions/salon/postSalonData.types';
import { SEND_SMS_SUCCESS } from 'actions/sms/bePutClientSmsText.types';
import bePutSalonFlagSettings from 'actions/sms/bePutSalonFlagSettings.action';
import { abeStylists } from 'actions/stylist/beSalonStylists.action';
import { feSalonUpdate } from 'actions/ui.actions';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToLowerCase from 'utils/converters/any/fromAnyToLowerCase.util';
import fromActionToMessage from 'utils/converters/backend/fromActionToMessage.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util'; // eslint-disable-line no-unused-vars

import { BOOKING_UPDATE_CLIENT_LIST_SUCCESS } from 'booking/constants';
import {
  ADD_STYLIST_SUCCESS,
  CLIENT_LIST_FAILURE,
  CLIENT_LIST_REQUEST,
  CLIENT_LIST_SUCCESS,
  CLIENT_PUSHER,
  CURRENT_SALON_RESET,
  currentSalonInit,
  DELETE_STYLIST_SUCCESS,
  EDIT_STYLIST_SUCCESS,
  GET_CATEGORIES_SUCCESS,
  REORDER_STYLISTS,
  SALON_DETAILS_ERROR,
  SALON_DETAILS_REQUEST,
  SALON_DETAILS_SUCCESS,
  SALON_STRIPE_SETUP,
  UPDATE_SALON_DATA_FAILURE,
  UPDATE_SALON_DATA_REQUEST,
  UPDATE_SALON_DATA_SUCCESS,
} from 'lib/constants/currentSalon';
import { putInList } from 'lib/redux_functions';
import { GET_STYLISTS_FAILURE, GET_STYLISTS_REQUEST, GET_STYLISTS_SUCCESS } from 'onboarding/stylists/actions';
import {
  CREATE_PIN_SUCCESS,
  REMOVE_FB_INTEGRATION_FAILURE,
  REMOVE_FB_INTEGRATION_REQUEST,
  REMOVE_FB_INTEGRATION_SUCCESS,
} from 'salon/constants';
import {
  HIDE_NON_WORKING_STYLIST_COLUMN_SUCCESS,
  UPDATE_NO_SHOW_BEHAVIOUR_SUCCESS,
} from 'settings/noshowvisibility/actions';
import { GET_SALON_ONLINE_SETTINGS_SUCCESS, SET_NOSHOW_OR_DEPOSIT_SUCCESS } from 'settings/online-booking/actions';

const L = logger('currentSalonReducer');

function tidyOpeningHours(response) {
  const openingHours = { 0: {}, 1: {}, 2: {}, 3: {}, 4: {}, 5: {}, 6: {} };
  Object.keys(response).forEach((key) => {
    const found = key.match(/^(mon|tue|wed|thu|fri|sat|sun)_(.*)/);
    if (found) {
      let weekday = 0;
      // use the same weekday mapping as moment.js uses in moment().day()
      switch (found[1]) {
        case 'mon':
          weekday = 1;
          break;
        case 'tue':
          weekday = 2;
          break;
        case 'wed':
          weekday = 3;
          break;
        case 'thu':
          weekday = 4;
          break;
        case 'fri':
          weekday = 5;
          break;
        case 'sat':
          weekday = 6;
          break;
        case 'sun':
          weekday = 0;
          break;
        default:
          break;
      }
      openingHours[weekday][found[2]] = response[found[0]]; // eslint-disable-line prefer-destructuring
      openingHours[weekday].shortName = found[1]; // eslint-disable-line prefer-destructuring
    }
  });
  const arrayOpeningHours = [];
  for (let i = 0; i < 7; i += 1) {
    arrayOpeningHours.push(openingHours[i]);
  }
  return arrayOpeningHours;
}

const fromPlanIdToPlanName = (planId) => {
  if (1 === planId) return 'Essential';
  if (2 === planId) return 'Advanced';
  if (3 === planId) return 'Supercharged';
  return '';
};

const currentSalonReducer = (state = currentSalonInit(), action) => {
  const { type, is, response, params, next, error, errorMessage, first, data: actionData } = asObject(action);
  if (!type) return state;

  if (type === feSalonUpdate.AT.name) {
    const newState = {
      ...state,
      ...actionData,
    };

    if (asArray(state.openingHours)) {
      const updatedOpeningHours = state.openingHours.map((day) => {
        const short = day.shortName;
        const openKey = `${short}OpenTime`;
        const closeKey = `${short}CloseTime`;
        const isOpenKey = `${short}Open`;
        const updatedDay = { ...asObject(day) };

        if (actionData[openKey]) {
          updatedDay.open_time = actionData[openKey];
        }
        if (actionData[closeKey]) {
          updatedDay.close_time = actionData[closeKey];
        }
        if (isOpenKey in actionData) {
          updatedDay.open = actionData[isOpenKey];
        }

        return updatedDay;
      });

      newState.openingHours = updatedOpeningHours;
    }

    L.debug('REDUCER currentSalonReducer', 'newState->', newState, 'actionData->', actionData);

    return newState;
  }

  const rv1 = asObject(response); // response version 1 - old version
  const { json: rv3, originalJson: rv3og } = asObject(action); // response version 3 - new version

  switch (type) {
    case feSetThemeMode.AT.name:
      return { ...state, calendar_mode: actionData };

    case bePutSalonFlagSettings.AT.init:
      return { ...state, isFetching: true };
    case bePutSalonFlagSettings.AT.pass:
      return is.ok ? { ...state, ...rv3 } : { ...state, errorMessage: fromActionToMessage(action) };
    case bePutSalonFlagSettings.AT.fail:
      return { ...state, errorMessage: fromActionToMessage(action) };
    case bePutSalonFlagSettings.AT.done:
      return { ...state, isFetching: false };

    case CURRENT_SALON_RESET:
      return currentSalonInit();

    case GET_CATEGORIES_SUCCESS:
      return { ...state, services: rv1, lists: { ...asObject(state?.lists), services: rv1 } };

    case SET_NOSHOW_OR_DEPOSIT_SUCCESS: {
      const newState = state;
      const entries = Object.keys(rv1);

      for (let i = 0; i < entries.length; i += 1) {
        newState[entries[i]] = rv1[entries[i]];
      }
      return { ...state, ...newState };
    }
    case SALON_DETAILS_REQUEST:
      return { ...state, isFetching: true, needs_card: false };
    case UPDATE_SALON_DATA_REQUEST:
      return { ...state, isPosting: true };
    case UPDATE_SALON_DATA_FAILURE:
      return { ...state, isPosting: false };
    case UPDATE_SALON_DATA_SUCCESS:
    case SALON_DETAILS_SUCCESS:
    case POST_SALON_DATA_SUCCESS:
      return { ...state, ...rv1, isFetching: false, openingHours: tidyOpeningHours(rv1), isPosting: false };
    case SALON_DETAILS_ERROR:
      return { ...state, isFetching: false, errorMessage, error };
    case GET_STYLISTS_REQUEST:
      return { ...state, requestedStylists: true, isFetchingStylists: true };
    case GET_STYLISTS_FAILURE:
      return { ...state, isFetchingStylists: false };
    case CREATE_PIN_SUCCESS: {
      const stylists = state.stylistList;
      const pinStylist = asArray(stylists).find((stylist) => stylist?.id === rv1?.stylist);
      if (pinStylist) pinStylist.has_pin_login = true; // FIXME: the stylist object should be replaced, not modified
      return { ...state, stylistList: stylists, lists: { ...asObject(state?.lists), stylists } };
    }
    case abeStylists.AT.pass:
      return { ...state, stylistList: rv3og, lists: { ...asObject(state?.lists), stylists: rv3og } };
    case GET_STYLISTS_SUCCESS:
      return {
        ...state,
        stylistList: rv1,
        lists: { ...asObject(state?.lists), stylists: rv1 },
        isFetchingStylists: false,
      };
    case DELETE_STYLIST_SUCCESS: {
      const stylists = state.stylistList.slice();
      for (let i = 0; i < stylists.length; i += 1) {
        if (stylists[i].id === params.stylistId) {
          stylists[i].status = 'deleted'; // FIXME: the object should be replaced, not modified
          break;
        }
      }
      return { ...state, stylistList: stylists, lists: { ...asObject(state?.lists), stylists } };
    }
    case EDIT_STYLIST_SUCCESS: {
      const stylists = state.stylistList.slice();

      for (let i = 0; i < stylists.length; i += 1) {
        if (stylists[i].id === rv1.id) {
          stylists[i] = rv1;
          break;
        }
      }

      return { ...state, stylistList: stylists, lists: { ...asObject(state?.lists), stylists } };
    }
    case ADD_STYLIST_SUCCESS: {
      const stylists = state.stylistList.slice();
      if (!stylists.find((value) => value.id && rv1 && rv1.id && value.id === rv1.id)) {
        stylists.push(rv1);
      }
      return { ...state, stylistList: stylists, lists: { ...asObject(state?.lists), stylists } };
    }
    case CLIENT_LIST_REQUEST:
      return { ...state, isFetchingClientList: true, requestedClientList: true };
    case CLIENT_LIST_FAILURE:
      return { ...state, isFetchingClientList: false };
    case CLIENT_LIST_SUCCESS:
      return {
        ...state,
        clientList: rv1,
        lists: { ...asObject(state?.lists), clients: rv1 },
        isFetchingClientList: false,
      };

    case CLIENT_PUSHER: {
      const clientData = actionData.data.data;
      const clientAction = actionData.action;

      if (clientAction === 'UPDATE') {
        const clients = asArray(state.clientList)
          .map((item) => (item?.id === clientData?.id ? clientData : item))
          .sort((a, b) => (fromAnyToLowerCase(a?.user_name) < fromAnyToLowerCase(b?.user_name) ? -1 : 1));

        return { ...state, clientList: clients, lists: { ...asObject(state?.lists), clients } };
      }

      if (clientAction === 'CREATE') {
        const comparator = (a, b) => (fromAnyToLowerCase(a.user_name) < fromAnyToLowerCase(b.user_name) ? -1 : 1);
        const clients = [...asArray(state.clientList), clientData].sort(comparator);
        return { ...state, clientList: clients, lists: { ...asObject(state?.lists), clients } };
      }

      if (clientAction === 'DELETE') {
        const clients = asArray(state.clientList).filter((item) => item?.id !== clientData?.id);
        return { ...state, clientList: clients, lists: { ...asObject(state?.lists), clients } };
      }

      return state;
    }

    case BOOKING_UPDATE_CLIENT_LIST_SUCCESS:
      return { ...state, clientList: putInList(state.clientList.slice(), rv1) };

    case REORDER_STYLISTS: {
      const from = state.stylistList.findIndex((element) => element.id === first.id);
      const to = state.stylistList.findIndex((element) => element.id === next.id);

      const stylists = state.stylistList.slice();
      stylists.splice(to, 0, stylists.splice(from, 1)[0]);

      return { ...state, stylistList: stylists, lists: { ...asObject(state?.lists), stylists } };
    }

    case UPDATE_NO_SHOW_BEHAVIOUR_SUCCESS:
      return { ...state, ...rv1 };

    case SALON_STRIPE_SETUP: {
      let ncobSettingData;
      if (actionData.action === 'CREATE') {
        if (actionData.status === 200) {
          ncobSettingData = actionData.data;
        }
      }
      return { ...state, ...ncobSettingData, isFetching: false };
    }

    case GET_SALON_ONLINE_SETTINGS_SUCCESS:
      return { ...state, online_booking: rv1.online_booking, ncob_tiny_url: rv1.ncob_tiny_url };
    case REMOVE_FB_INTEGRATION_REQUEST:
      return { ...state, isFetching: true };
    case REMOVE_FB_INTEGRATION_SUCCESS:
      return { ...state, isFetching: false, fb_details: null };

    case REMOVE_FB_INTEGRATION_FAILURE:
      return { ...state, isFetching: false, errorMessage };

    case beUpgradePlan.AT.init:
      return { ...state, pricing_plan_id: params.newId };
    case beUpgradePlan.AT.pass: {
      return {
        ...state,
        pricing_plan: fromPlanIdToPlanName(state.pricing_plan_id),
        pricing_plan_id: state.pricing_plan_id,
      };
    }
    case HIDE_NON_WORKING_STYLIST_COLUMN_SUCCESS:
      return { ...state, hide_non_working_stylist_column: rv1.hide_non_working_stylist_column };
    case SEND_SMS_SUCCESS:
      return { ...state, digital_card_add_on: rv3.digital_card_add_on };
    default:
      return state;
  }
};

export default currentSalonReducer;
